import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import { setTheme } from '@/Redux/Slices/uiSlice';
import { THEME_PREFERENCES, type ThemePreference } from '@/lib/theme';

const ICONS: Record<ThemePreference, typeof Sun> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
};

/**
 * Theme control — system, light, dark, in that order.
 *
 * A cycling button rather than a menu, to match the language switcher beside it
 * and because three states is the most a cycle can carry before people start
 * clicking past the one they wanted. `system` leads the cycle because it is the
 * default, so one press from the default is "light" and two is "dark".
 *
 * The accessible name states the *current* mode, not the next one. A control
 * whose label is its own consequence ("Switch to dark") reads as the current
 * state to anyone arriving on it out of context, and gets the answer backwards.
 */
export function ThemeToggle({ showLabel = false }: { showLabel?: boolean }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const preference = useAppSelector((s) => s.ui.theme);

  const Icon = ICONS[preference];
  const label = t(`theme.${preference}`);
  const next = THEME_PREFERENCES[(THEME_PREFERENCES.indexOf(preference) + 1) % THEME_PREFERENCES.length];

  return (
    <Button
      variant="ghost"
      size={showLabel ? 'sm' : 'icon'}
      onClick={() => dispatch(setTheme(next))}
      title={`${t('theme.label')}: ${label}`}
      aria-label={`${t('theme.label')}: ${label}`}
      data-testid="theme-toggle"
    >
      <Icon className="h-4 w-4" aria-hidden />
      {showLabel && <span>{label}</span>}
    </Button>
  );
}
